import React, { useMemo, useState } from "react";
import { downloadFile } from "@/lib/api";
import { Card, Badge, Button, Empty } from "@/components/ui-kit";
import { LayoutGrid, Clock, FileText, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import ExerciseSheetModal from "@/components/ExerciseSheetModal";

const ZONE_TINTS = ["bg-primary/70", "bg-sky-500/70", "bg-yellow-500/70", "bg-purple-500/70", "bg-red-500/70", "bg-emerald-500/70", "bg-orange-500/70", "bg-pink-500/70"];

const ReadPitch = ({ zones, exercises, onZoneClick }) => {
  const nameOf = (id) => exercises.find((e) => e.id === id)?.name || "Exercice supprimé";
  const distinct = [...new Set(Object.values(zones))];
  const tintOf = (id) => ZONE_TINTS[distinct.indexOf(id) % ZONE_TINTS.length];
  return (
    <div className="relative rounded-lg overflow-hidden border-2 border-white/70 shadow-inner" style={{ aspectRatio: "5 / 3", background: "linear-gradient(180deg,#16a34a,#15803d)" }}>
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-2">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((z) => {
          const exId = zones[z];
          return (
            <div key={z} data-testid={`view-zone-${z}`}
              onClick={exId ? () => onZoneClick(exId) : undefined}
              title={exId ? "Voir la fiche de l'exercice" : ""}
              className={`relative border border-white/25 flex items-center justify-center p-1 text-center transition-colors ${exId ? `${tintOf(exId)} text-white ring-1 ring-inset ring-white/60 cursor-pointer hover:brightness-110` : "text-white/50"}`}>
              {exId ? <span className="text-[10px] font-bold leading-tight line-clamp-3 underline decoration-white/40">{nameOf(exId)}</span> : <span className="text-sm font-black">{z}</span>}
            </div>
          );
        })}
      </div>
      <div className="pointer-events-none absolute left-1/2 top-0 bottom-0 w-px bg-white/50 -translate-x-1/2" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 w-10 h-10 rounded-full border border-white/50 -translate-x-1/2 -translate-y-1/2" />
    </div>
  );
};

export default function SessionView({ event, exercises, canPdf }) {
  const [sheet, setSheet] = useState(null);

  const pitches = useMemo(() => {
    if (!event) return [];
    if (event.session_pitches && event.session_pitches.length) {
      return event.session_pitches.map((p) => ({ name: p.name || "Terrain", duration: p.duration || 0, zones: p.zones || {} }));
    }
    if (event.exercise_zones && Object.keys(event.exercise_zones).length) {
      const zones = {};
      Object.entries(event.exercise_zones).forEach(([exId, zs]) => (zs || []).forEach((z) => { zones[z] = exId; }));
      const dur = Object.values(event.exercise_durations || {}).reduce((s, d) => s + (d || 0), 0) || 60;
      return [{ name: "Terrain 1", duration: dur, zones }];
    }
    return [];
  }, [event]);

  const total = pitches.reduce((s, p) => s + (parseInt(p.duration) || 0), 0);
  const exOf = (id) => exercises.find((e) => e.id === id);
  const looseIds = (!pitches.length && event && (event.exercise_ids || []).length) ? event.exercise_ids : [];

  if (!pitches.length && !looseIds.length) {
    return <Empty text="Aucune séance préparée pour cet entraînement." />;
  }

  return (
    <div data-testid="session-view">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
          <Badge color="green">{pitches.length} terrain(s)</Badge>
          <span className="flex items-center gap-1"><Clock size={14} className="text-primary" /> {total} min au total</span>
        </div>
        {canPdf && (
          <Button data-testid="session-view-pdf" variant="outline"
            onClick={() => downloadFile(`/events/${event.id}/exercises.pdf`, `seance_${event.date || ""}.pdf`).catch(() => toast.error("Export échoué"))}>
            <FileText size={15} /> Télécharger la séance (PDF)
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-3 italic">Cliquez sur un exercice placé sur le terrain pour voir sa fiche complète.</p>

      {looseIds.length > 0 && (
        <Card data-testid="session-view-loose" className="mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><Dumbbell size={13} className="text-primary" /> Exercices de la séance</p>
          <div className="flex flex-col gap-1.5">
            {looseIds.map((id) => (
              <button key={id} data-testid={`session-view-ex-${id}`} onClick={() => setSheet(exOf(id))}
                className="flex items-center gap-2 py-2 px-3 rounded-lg bg-secondary/50 border border-border hover:border-primary/50 hover:text-primary transition-colors text-left">
                <Dumbbell size={13} className="text-primary shrink-0" />
                <span className="font-semibold text-sm">{exOf(id)?.name || "Exercice supprimé"}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="space-y-4">
        {pitches.map((p, i) => {
          const distinct = [...new Set(Object.values(p.zones))];
          const rot = distinct.length && parseInt(p.duration) ? Math.round(parseInt(p.duration) / distinct.length) : parseInt(p.duration) || 0;
          return (
            <Card key={i} data-testid="session-view-pitch" className="border-primary/40">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2"><LayoutGrid size={16} className="text-primary" /><span className="font-bold">{p.name}</span></div>
                <span className="text-sm flex items-center gap-1.5"><Clock size={14} className="text-primary" /> {p.duration} min{distinct.length > 1 ? ` · rotation ≈ ${rot} min/ex.` : ""}</span>
              </div>
              <ReadPitch zones={p.zones} exercises={exercises} onZoneClick={(id) => setSheet(exOf(id))} />
              <div className="mt-3 flex items-center gap-1.5 flex-wrap text-xs">
                {distinct.map((id) => (
                  <button key={id} data-testid={`session-view-ex-${id}`} onClick={() => setSheet(exOf(id))}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-secondary hover:bg-primary/20 hover:text-primary transition-colors font-semibold">
                    <Dumbbell size={12} /> {exOf(id)?.name || "Exercice supprimé"}
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      {(event.session_comment || event.session_note) && (
        <Card className="mt-4">
          {event.session_comment && <p className="text-sm mb-2"><span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Commentaire : </span>{event.session_comment}</p>}
          {event.session_note && <p className="text-sm"><span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Note : </span>{event.session_note}</p>}
        </Card>
      )}

      {sheet && <ExerciseSheetModal exercise={sheet} onClose={() => setSheet(null)} />}
    </div>
  );
}
